import { useSelector } from 'react-redux';
import { useSWRConfig } from 'swr';
import useSWRImmutable from 'swr/immutable';
import {
    getNitrogenSourcesFromAPI,
    getNSRecommendationFromAPI,
    getNSRecommendationsFromAPI,
} from '/@/store/nitrogen-recommendation/apiProviders';
import { getSessionCountry } from '/@/store/session/selectors';

/**
 * @typedef {Object} NitrogenRecommendation
 * @property {'fixed'|string} type
 * @property {string} technique
 * @property {string} plantingDate
 * @property {string} phone
 * @property {string} distributor
 * @property {string} estimatedApplicationDate
 * @property {string | undefined} [applicationDate]
 * @property {number} hybrid
 * @property {ManagementZones[]} managementZones
 * @property {string} companyName
 * @property {number} nitrogenSource
 * @property {{file: string, recommendation: string}[]} [results]
 * @property {any[]} managementZoneFilesIds
 * @property {string} field
 * @property {string} status
 * @property {number} appInstance
 * @property {string} id
 * @property {string} created
 * @property {string} _id
 * @property {string} _rev
 * @property {ManagementZoneFiles[]} [managementZoneFiles]
 */

/**
 * @typedef {{id: string, file: string}} ManagementZoneFiles
 */

/**
 * @typedef {Object} ICountry
 * @property {object} altitude
 * @property {Bounds} bounds
 * @property {string} code
 * @property {number} id
 * @property {string} language
 * @property {string} name
 * @property {object} parent
 */

/**
 * @typedef {Object} Bounds
 * @property {number} east
 * @property {number} north
 * @property {number} south
 * @property {number} west
 * @returns {ICountry}
 */
export const useCountry = () => useSelector(getSessionCountry);

export const useNSRecommendationById = (recoId) => {
    const { cache } = useSWRConfig();
    let key = {
        key: 'myNSRecommendations',
        recoId,
    };
    if (!recoId) key = null;
    return useSWRImmutable(key, (recoId) =>
        getNSRecommendationFromAPI(
            {
                key: 'myNSRecommendations',
                recoId,
            },
            cache
        )
    );
};

export const useNSRecommendations = () => {
    const country = useSelector(getSessionCountry);
    const key = {
        key: 'allMyNSRecommendations',
        country: country.code,
    };
    return useSWRImmutable(key, getNSRecommendationsFromAPI);
};

export const useNitrogenSources = () => {
    const key = 'nitrogenSources';
    return useSWRImmutable(key, () => getNitrogenSourcesFromAPI());
};

export const useNitrogenSource = (id: number) => {
    const sources = useNitrogenSources();
    return sources.data?.find((source) => source.id === id);
};
